import { Alert, Badge, Button, Card, Field, Textarea } from "@artisan/ui";
import { Check, Eraser, PenLine } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import { PlanView } from "../components/plan-view";
import { Screen } from "../components/screen";
import { nowIso } from "../lib/time";
import { projectTitle } from "../project/actions";
import { nextId, wallLabel } from "../project/model";
import { storePhoto } from "../project/photos";
import { photoBlob, updateProject, useProject } from "../project/store";
import { errorsOf, reviewProject } from "../survey/checks";
import { umToMmText } from "../survey/units";

const W = 1200;
const H = 400;
const STATEMENT = "Хэмжээ, загвар, материалын сонголтыг зөвшөөрч байна.";

function SignaturePad({ onChange }: { onChange: (empty: boolean) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const last = useRef<[number, number] | null>(null);
  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, W, H);
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111114";
  }, []);
  const at = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
  };
  return (
    <canvas
      ref={canvas}
      width={W}
      height={H}
      aria-label="Гарын үсэг зурах талбар"
      data-testid="signature-pad"
      className="aspect-[3/1] w-full touch-none rounded-xl border-2 border-dashed bg-white"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        last.current = at(e);
      }}
      onPointerMove={(e) => {
        if (!last.current) return;
        const ctx = e.currentTarget.getContext("2d");
        const p = at(e);
        if (!ctx) return;
        ctx.beginPath();
        ctx.moveTo(last.current[0], last.current[1]);
        ctx.lineTo(p[0], p[1]);
        ctx.stroke();
        last.current = p;
        onChange(false);
      }}
      onPointerUp={() => (last.current = null)}
    />
  );
}

export function SignScreen() {
  const { id } = useParams();
  const project = useProject(id);
  const navigate = useNavigate();
  const [name, setName] = useState(project?.sign_off?.name ?? project?.site.customer_name ?? "");
  const [statement, setStatement] = useState(project?.sign_off?.statement ?? STATEMENT);
  const [empty, setEmpty] = useState(true);
  const [padKey, setPadKey] = useState(0);
  const [sigUrl, setSigUrl] = useState<string | null>(null);
  const sigPhoto = project?.photos.find((p) => p.id === project.sign_off?.photo_id);
  useEffect(() => {
    let url: string | null = null;
    if (sigPhoto)
      void photoBlob(sigPhoto).then((b) => {
        if (b) setSigUrl((url = URL.createObjectURL(b)));
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [sigPhoto]);
  if (!project) return <Navigate to="/" replace />;
  const current = project;
  const errors = errorsOf(reviewProject(project)).filter((e) => e.code !== "sign_off_without_render");

  async function save() {
    const canvas = document.querySelector<HTMLCanvasElement>("[data-testid=signature-pad]");
    if (!canvas) return;
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("png"))), "image/png"));
    let photoId = "";
    await updateProject(current.id, (p) => {
      photoId = nextId(p, "SIG");
    });
    const ref = await storePhoto({ blob, width: W, height: H, content_type: "image/png" }, { id: photoId, caption: "Гарын үсэг" });
    await updateProject(current.id, (p) => {
      const old = p.sign_off?.photo_id;
      if (old) p.photos = p.photos.filter((x) => x.id !== old);
      p.photos.push(ref);
      p.sign_off = { name: name.trim(), signed_at: nowIso(), statement: statement.trim(), photo_id: ref.id };
    });
    navigate(`/p/${current.id}/send`);
  }

  return (
    <Screen back={`/p/${project.id}`} title="Харилцагч батлах" subtitle={projectTitle(project)}>
      <div className="grid grid-cols-[1fr_1.15fr] gap-6">
        <Card className="space-y-4 p-6">
          <h2 className="font-semibold">Юуг батлах вэ</h2>
          {project.render.approved?.output_url ? (
            <img src={project.render.approved.output_url} alt="Батлагдсан render" className="w-full rounded-lg" />
          ) : (
            <Alert variant="warning">Батлагдсан render алга. Render хэсэгт харилцагчаар зургаа батлуулж болно.</Alert>
          )}
          {project.rooms.map((room) => (
            <div key={room.id} className="flex gap-4 rounded-lg border p-3">
              <PlanView room={room} compact className="h-24 w-28 shrink-0" />
              <div className="min-w-0 text-sm">
                <p className="font-medium">{room.name}</p>
                <p className="font-mono text-xs text-muted-foreground">
                  {room.walls.map((w, i) => `${wallLabel(i)} ${w ? umToMmText(w.um) : "?"}`).join(" · ")}
                </p>
                <p className="text-xs text-muted-foreground">
                  Тааз {room.ceiling ? umToMmText(room.ceiling.um) : "?"} мм · {room.zones.length} бүс · {room.openings.length} нээлхий
                </p>
              </div>
            </div>
          ))}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {Object.entries(project.choices)
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-muted-foreground">{{ carcass: "Корпус", front: "Нүүр", handle: "Бариул", worktop: "Тавцан", style: "Хэв маяг" }[k] ?? k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
          </dl>
        </Card>
        <Card className="space-y-4 p-6">
          {project.sign_off ? (
            <div className="space-y-2">
              <Badge variant="success" icon={Check}>
                {project.sign_off.name} гарын үсэг зурсан
              </Badge>
              {sigUrl ? <img src={sigUrl} alt="Гарын үсэг" className="w-full rounded-lg border" /> : null}
              <p className="text-xs text-muted-foreground">Дахин зурвал өмнөхийг солино.</p>
            </div>
          ) : null}
          {errors[0] ? <Alert variant="warning" title="Хэмжилт дутуу байна">{errors[0].message}</Alert> : null}
          <Field label="Харилцагчийн нэр" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="grid gap-1.5 text-sm font-medium">
            Зөвшөөрлийн үг
            <Textarea rows={2} value={statement} onChange={(e) => setStatement(e.target.value)} maxLength={2000} />
          </label>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium">
                <PenLine className="size-4" aria-hidden /> Гарын үсэг
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={Eraser}
                onClick={() => {
                  setPadKey((k) => k + 1);
                  setEmpty(true);
                }}
              >
                Арилгах
              </Button>
            </div>
            <SignaturePad key={padKey} onChange={setEmpty} />
          </div>
          <Button size="lg" className="w-full" icon={Check} disabled={empty || !name.trim()} onClick={() => void save()}>
            Батлаад хадгалах
          </Button>
        </Card>
      </div>
    </Screen>
  );
}
